import { useMemo, useState } from 'react';
import {
  isSelectable,
  pick,
  type KitchenCatalog,
  type KitchenMaterial,
  type MaterialType,
  type StockStatus,
  type ZoneKey,
} from '@cfg/shared';
import { Badge, Swatch } from '@cfg/ui';
import { useI18n, type DictKey } from '../i18n';
import { rovingKeyDown } from '../roving';
import { zoneMaterials } from '../state';

interface MaterialBrowserProps {
  catalog: KitchenCatalog;
  zone: ZoneKey;
  selected: KitchenMaterial | undefined;
  pending: string | null;
  onSelect: (material: KitchenMaterial) => void;
  onPreload: (material: KitchenMaterial) => void;
}

export function stockTone(status: StockStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  return status === 'in_stock'
    ? 'success'
    : status === 'low_stock'
      ? 'warning'
      : status === 'out_of_stock'
        ? 'danger'
        : 'neutral';
}

export function stockLabelKey(status: StockStatus): DictKey {
  return status === 'in_stock'
    ? 'inStock'
    : status === 'low_stock'
      ? 'lowStock'
      : status === 'out_of_stock'
        ? 'outOfStock'
        : 'onRequest';
}

const COLOR_ORDER = [
  'white',
  'beige',
  'wood',
  'brown',
  'grey',
  'black',
  'green',
  'blue',
  'red',
  'pink',
  'yellow',
];
const COLOR_DOTS: Record<string, string> = {
  white: '#f4f2ee',
  beige: '#d8c7a8',
  wood: '#b08355',
  brown: '#6f4b32',
  grey: '#9a9a9a',
  black: '#222222',
  green: '#5f7d5c',
  blue: '#4a6480',
  red: '#a8443a',
  pink: '#d9a3a3',
  yellow: '#d8b54a',
};

export function MaterialBrowser({
  catalog,
  zone,
  selected,
  pending,
  onSelect,
  onPreload,
}: MaterialBrowserProps) {
  const { t, locale } = useI18n();
  const [query, setQuery] = useState('');
  const [type, setType] = useState<MaterialType | 'all'>('all');
  const [color, setColor] = useState<string>('all');
  const [inStockOnly, setInStockOnly] = useState(false);
  const all = useMemo(() => zoneMaterials(catalog, zone), [catalog, zone]);
  const types = useMemo(() => [...new Set(all.map((m) => m.type))], [all]);
  const colors = useMemo(
    () =>
      COLOR_ORDER.filter((c) => all.some((m) => m.colorFamily === c)).concat(
        [...new Set(all.map((m) => m.colorFamily))].filter((c) => !COLOR_ORDER.includes(c)),
      ),
    [all],
  );
  const zoneName = pick(catalog.zones.find((z) => z.key === zone)?.name, locale);
  const colorLabel = (c: string) => {
    const key = `color_${c}` as DictKey;
    const label = t(key);
    return label === key ? c : label;
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter(
      (m) =>
        (type === 'all' || m.type === type) &&
        (color === 'all' || m.colorFamily === color) &&
        (!inStockOnly || m.stock.status === 'in_stock' || m.stock.status === 'low_stock') &&
        (!q ||
          m.name.en.toLowerCase().includes(q) ||
          m.name.he.includes(q) ||
          m.code.toLowerCase().includes(q) ||
          m.collection.toLowerCase().includes(q)),
    );
  }, [all, query, type, color, inStockOnly]);

  const effectiveType = types.includes(type as MaterialType) ? type : 'all';
  // the swatches form one tab stop (the chosen material, else the first one a visitor can pick);
  // the arrow keys move inside the grid (WAI-ARIA radio group)
  const tabStop = (
    visible.some((m) => m.id === selected?.id)
      ? selected
      : visible.find((m) => isSelectable(m, catalog.settings))
  )?.id;

  return (
    <section
      id="material-panel"
      role="tabpanel"
      aria-labelledby={`zone-tab-${zone}`}
      className="materials"
    >
      <div className="materials__header">
        <h2 className="materials__title">{zoneName}</h2>
        <input
          type="search"
          className="cfg-input materials__search"
          placeholder={t('search')}
          aria-label={t('search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {types.length > 1 && (
          <div className="chips" role="group" aria-label={t('filters')}>
            {(['all', ...types] as const).map((ty) => (
              <button
                key={ty}
                type="button"
                className="chip"
                aria-pressed={effectiveType === ty}
                onClick={() => setType(ty)}
              >
                {ty === 'all' ? t('all') : t(`type_${ty}` as DictKey)}
              </button>
            ))}
          </div>
        )}
        <div className="chips chips--colors" role="group" aria-label={t('colorFamily')}>
          {colors.length > 1 &&
            colors.map((c) => (
              <button
                key={c}
                type="button"
                className="chip chip--color"
                aria-pressed={color === c}
                title={colorLabel(c)}
                onClick={() => setColor(color === c ? 'all' : c)}
              >
                <span
                  className="chip__dot"
                  style={{ background: COLOR_DOTS[c] ?? '#9a9a9a' }}
                  aria-hidden
                />
                <span className="chip__label">{colorLabel(c)}</span>
              </button>
            ))}
          <button
            type="button"
            className="chip"
            aria-pressed={inStockOnly}
            onClick={() => setInStockOnly((v) => !v)}
          >
            {t('inStockOnly')}
          </button>
        </div>
      </div>

      {selected && (
        <div className="materials__selected" aria-live="polite">
          <span
            className="materials__selected-thumb"
            style={{ backgroundImage: `url("${selected.textures.thumbnail}")` }}
            aria-hidden
          />
          <div className="materials__selected-body">
            <strong>{pick(selected.name, locale)}</strong>
            <span className="muted">
              {selected.collection} · {t(`finish_${selected.finish}` as DictKey)} · {selected.code}
            </span>
            <span>
              <Badge tone={stockTone(selected.stock.status)}>
                {t(stockLabelKey(selected.stock.status))}
              </Badge>
            </span>
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="muted materials__empty">{t('noResults')}</p>
      ) : (
        <div
          className="cfg-swatch-grid materials__grid"
          role="radiogroup"
          aria-label={t('materials')}
          onKeyDown={(e) => rovingKeyDown(e, { selector: '[role="radio"]', activate: true })}
        >
          {visible.map((m) => {
            const selectable = isSelectable(m, catalog.settings);
            const showBadge = m.stock.status === 'out_of_stock' || m.stock.status === 'low_stock';
            return (
              <Swatch
                key={m.id}
                image={m.textures.thumbnail}
                label={pick(m.name, locale)}
                description={`${m.collection}, ${t(stockLabelKey(m.stock.status))}`}
                selected={selected?.id === m.id}
                tabIndex={m.id === tabStop ? 0 : -1}
                disabled={!selectable}
                loading={pending === m.id}
                badge={
                  showBadge ? (
                    <Badge tone={stockTone(m.stock.status)}>
                      {t(stockLabelKey(m.stock.status))}
                    </Badge>
                  ) : undefined
                }
                onSelect={() => onSelect(m)}
                onPreview={() => onPreload(m)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
